// SETTINGS V2 — mint-form fields, shared by the guest-pass and demo-user paths
// (split out of IssueGuestPassDialogV2 for the 300-line rule when
// DEMO_USER_TYPE_SPEC F1 landed). Presentational: all state lives in the dialog.
//
// The demo path differs in wording only. Demo passes send NO email (owner ruling
// 2026-08-04 — the PIN is read off the screen), so the address field is labelled
// as what it now is: a record of who the pass was for, stored because GuestEmail
// is NOT NULL. Nothing is delivered to it. Same shape otherwise.
//
// v2 atoms only. Inline styles + scoped CSS-var tokens; no MUI/sx/dt/Tailwind.
import React from "react";
import type { TFunction } from "i18next";

import type { FormSelectOption } from "../../../../shared/components/redesign/forms/FormSelect";
import { SInputField, SSelectField } from "../atoms";
import type { GuestPassFormErrors, GuestPassFormValues } from "./guestPassFormUtils";

export interface GuestPassFormFieldsV2Props {
  values: GuestPassFormValues;
  onFieldChange: (key: keyof GuestPassFormValues, value: string) => void;
  onFieldTouched: (key: string) => void;
  errors: GuestPassFormErrors;
  machineOptions: FormSelectOption[];
  durationOptions: FormSelectOption[];
  visitPurposeMax: number;
  isDemo: boolean;
  isPhone: boolean;
  t: TFunction;
  tAccount: TFunction;
}

const GuestPassFormFieldsV2: React.FC<GuestPassFormFieldsV2Props> = ({
  values,
  onFieldChange,
  onFieldTouched,
  errors,
  machineOptions,
  durationOptions,
  visitPurposeMax,
  isDemo,
  isPhone,
  t,
  tAccount,
}) => {
  const twoCol: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 12,
  };

  return (
    <>
      <div onBlur={() => onFieldTouched("machineInternalNumber")}>
        <SSelectField
          label={t("guestPass.machine") as string}
          required
          value={values.machineInternalNumber}
          onChange={(v) => onFieldChange("machineInternalNumber", String(v))}
          options={machineOptions}
          placeholder={t("guestPass.selectMachine") as string}
          error={errors.machine}
        />
      </div>

      <div onBlur={() => onFieldTouched("guestEmail")}>
        <SInputField
          label={
            (isDemo ? t("guestPass.demoVisitorEmail") : t("guestPass.guestEmail")) as string
          }
          required
          mono
          value={values.guestEmail}
          onChange={(v) => onFieldChange("guestEmail", v)}
          placeholder={tAccount("emailPlaceholder") as string}
          error={errors.guestEmail}
          unit={isDemo ? (t("guestPass.demoVisitorEmailHint") as string) : undefined}
        />
      </div>

      <div style={twoCol}>
        <div onBlur={() => onFieldTouched("guestName")}>
          <SInputField
            label={(isDemo ? t("guestPass.demoVisitorName") : t("guestPass.guestName")) as string}
            required
            value={values.guestName}
            onChange={(v) => onFieldChange("guestName", v)}
            placeholder={t("guestPass.guestNamePlaceholder") as string}
            error={errors.guestName}
          />
        </div>
        <SSelectField
          label={t("guestPass.duration") as string}
          value={values.duration}
          onChange={(v) => onFieldChange("duration", String(v))}
          options={durationOptions}
        />
      </div>

      <div onBlur={() => onFieldTouched("visitPurpose")}>
        <SInputField
          label={t("guestPass.visitPurpose") as string}
          maxLength={visitPurposeMax}
          value={values.visitPurpose}
          onChange={(v) => onFieldChange("visitPurpose", v)}
          placeholder={t("guestPass.visitPurposePlaceholder") as string}
          error={errors.visitPurpose}
        />
      </div>
    </>
  );
};

export default GuestPassFormFieldsV2;
